import { Button, Card, CardContent, PageHeader, StatusTimeline, TextareaField } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { hasPermission } from "../../features/ops/permissions.ts";
import { useOrder, useUpdateOrderStatus } from "../../features/ops/queries.ts";
import type { OrderStatus, StaffPermission } from "../../features/ops/types.ts";
import { getStaffPrincipal } from "../../lib/auth-session.ts";
import { formatMoney } from "../../lib/format.ts";

export const Route = createFileRoute("/_app/orders/$orderId")({ component: OrderDetailScreen });

// FR-ROLE-* §3: accept/reject is a distinct permission from cancel and from
// the forward fulfilment-status transitions (preparing/ready/served).
const actionPermission: Record<OrderStatus, StaffPermission | null> = {
  pending: "order:accept",
  accepted: "order:fulfil",
  preparing: "order:fulfil",
  ready: "order:fulfil",
  served: null,
  cancelled: null,
  rejected: null,
};
const cancelLikePermission: Partial<Record<OrderStatus, StaffPermission>> = {
  rejected: "order:accept",
  cancelled: "order:cancel",
};

function OrderDetailScreen() {
  const principal = getStaffPrincipal();
  const { orderId } = Route.useParams();
  const { data: order } = useOrder(orderId);
  const update = useUpdateOrderStatus();
  const [reason, setReason] = useState("");
  if (!order) return <PageHeader title="Order detail" subtitle="Loading order…" />;
  const actions: OrderStatus[] =
    order.status === "pending"
      ? ["accepted", "rejected", "cancelled"]
      : order.status === "accepted"
        ? ["preparing", "cancelled"]
        : order.status === "preparing"
          ? ["ready", "cancelled"]
          : order.status === "ready"
            ? ["served"]
            : [];
  return (
    <div className="space-y-6">
      <PageHeader
        title={`Order ${order.id}`}
        subtitle={`${order.tableLabel} · ${order.station} · ${order.status}`}
      />
      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <Card>
          <CardContent className="space-y-4 p-5">
            <h2 className="font-display font-semibold text-lg">Items</h2>
            {order.lines.map((line) => (
              <div key={line.id} className="flex justify-between border-border border-b py-2">
                <span>
                  {line.qty}× {line.name} / {line.nameTh}
                  <br />
                  <span className="text-muted-foreground text-xs">
                    {line.modifiers?.join(", ")}
                  </span>
                </span>
                <span>{formatMoney(line.qty * line.priceMinor)}</span>
              </div>
            ))}
            <p className="font-semibold">Total {formatMoney(order.totalMinor)}</p>
            {order.note ? <p className="rounded-md bg-muted p-3 text-sm">{order.note}</p> : null}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-4 p-5">
            <h2 className="font-display font-semibold text-lg">Timeline</h2>
            <StatusTimeline
              items={order.timeline.map((entry) => ({
                id: entry.id,
                label: entry.action,
                timestamp: entry.createdAt,
                description: entry.reason,
              }))}
            />
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardContent className="space-y-4 p-5">
          <TextareaField
            id="reason"
            label="Reason for cancel/reject"
            value={reason}
            onChange={setReason}
            placeholder="Required for cancel/reject"
          />{" "}
          <div className="flex flex-wrap gap-2">
            {actions.map((status) => {
              const permission = cancelLikePermission[status] ?? actionPermission[status];
              const allowed = !permission || hasPermission(principal, permission);
              return (
                <Button
                  key={status}
                  variant={
                    status === "cancelled" || status === "rejected" ? "destructive" : "default"
                  }
                  disabled={!allowed}
                  title={allowed ? undefined : "Requires additional staff permission"}
                  onClick={() => update.mutate({ orderId: order.id, status, reason })}
                >
                  {status}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
